import { useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import AddTaskRoundedIcon from '@mui/icons-material/AddTaskRounded'
import LinkRoundedIcon from '@mui/icons-material/LinkRounded'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import { formatDay } from 'features/actions/workspace/remedialDates'
import { personLabel } from 'features/actions/workspace/ownerDisplay'
import { BORDER, INK, LINK, MUTED, SecondaryButton, TextButton } from 'features/actions/workspace/ui'
import LinkBadge from 'features/taskLinks/LinkBadge'
import LinkTaskDialog from 'features/taskLinks/LinkTaskDialog'
import RemedialSuggestions from 'features/taskLinks/RemedialSuggestions'
import taskLinksAPI from 'features/taskLinks/taskLinksAPI'
import { errorMessage } from 'features/taskLinks/linkCopy'
import { unlinkedToast } from 'features/taskLinks/UndoUnlinkToast'

const helpText = (handles, related) => {
  if (handles.length) {
    return related.length
      ? 'This remedial closes by itself when every "Handled by" task below is completed. "Related" tasks are for context only.'
      : 'This remedial closes by itself when every task below is completed.'
  }
  if (related.length) return '"Related" tasks are for context only. Create or link a task that handles this remedial to plan the fix.'
  return 'No task yet. Create a task to plan the work that puts this right, or link a task that already deals with it.'
}

const RemedialTasksPanel = ({ action, onAddTask, onChanged }) => {
  const [picking, setPicking] = useState(false)
  const [busy, setBusy] = useState(null)
  const tasks = Array.isArray(action?.linkedTasks) ? action.linkedTasks : []
  const handles = tasks.filter(task => (task.relation || 'handles') === 'handles')
  const related = tasks.filter(task => task.relation === 'related')
  const done = handles.filter(task => task.status === 'completed').length
  const canAdd = handles.length > 0 && !!action?.permissions?.canConvert
  const bpId = action?.buildingPassport?.id || action?.buildingPassportId
  const canLink = !!bpId && !!action?.permissions?.canView && action?.status !== 'dismissed' && !!onChanged

  const run = async (key, fn, message) => {
    setBusy(key)
    try {
      await fn()
      toast.success(message)
      onChanged?.()
    } catch (err) {
      toast.error(errorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Panel aria-labelledby={`remedial-tasks-${action?.id}`}>
      <Head>
        <Title id={`remedial-tasks-${action?.id}`}>Tasks</Title>
        {handles.length > 0 && <Progress>{done} of {handles.length} task{handles.length === 1 ? '' : 's'} done</Progress>}
      </Head>
      <Help>{helpText(handles, related)}</Help>
      {tasks.length > 0 && (
        <List>
          {tasks.map(task => {
            const status = taskStatusMeta(task.status)
            const people = (task.assignees || []).map(personLabel).filter(Boolean)
            return (
              <Item key={task.id}>
                <ItemHead>
                  <TaskLink to={`/tasks/${task.id}`}>{task.title || 'Untitled task'}</TaskLink>
                  {task.relation && <LinkBadge relation={task.relation} source={task.source} />}
                </ItemHead>
                <Meta>
                  <Status style={{ color: status.color, background: status.bg }}>{status.label}</Status>
                  <span>{people.length ? people.join(', ') : 'Not assigned yet'}</span>
                  <span>{task.deadline ? `Deadline ${formatDay(task.deadline)}` : 'No deadline set'}</span>
                  {canLink && task.relation && (
                    <UnlinkButton
                      type='button'
                      disabled={busy === task.id}
                      aria-label={`Unlink ${task.title || 'task'} from this remedial`}
                      onClick={() => run(task.id, () => taskLinksAPI.unlink(action.id, task.id), unlinkedToast({ text: 'Task unlinked. It will not be suggested again for this remedial.', actionId: action.id, taskId: task.id, relation: task.relation, onRestored: onChanged }))}
                    >
                      Unlink
                    </UnlinkButton>
                  )}
                </Meta>
              </Item>
            )
          })}
        </List>
      )}
      {canLink && action?.sourceFileId && <RemedialSuggestions action={action} onChanged={onChanged} />}
      {(canAdd || canLink) && (
        <Buttons>
          {canAdd && (
            <SecondaryButton type='button' onClick={() => onAddTask?.(action)}>
              <AddTaskRoundedIcon style={{ fontSize: 18 }} aria-hidden='true' />
              Add another task
            </SecondaryButton>
          )}
          {canLink && (
            <SecondaryButton type='button' onClick={() => setPicking(true)}>
              <LinkRoundedIcon style={{ fontSize: 18 }} aria-hidden='true' />
              Link an existing task
            </SecondaryButton>
          )}
        </Buttons>
      )}
      {canLink && (
        <LinkTaskDialog
          open={picking}
          bpId={bpId}
          remedialTitle={action?.title}
          excludeIds={tasks.map(task => task.id)}
          onClose={() => setPicking(false)}
          onPick={(task, relation) => {
            setPicking(false)
            run(`pick-${task.id}`, () => taskLinksAPI.link(action.id, task.id, relation), 'Task linked to this remedial.')
          }}
        />
      )}
    </Panel>
  )
}

export default RemedialTasksPanel

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin: 16px 0;
  padding: 16px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  background: #fff;
`

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`

const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
`

const Progress = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${MUTED};
`

const Help = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${MUTED};
`

const List = styled.ul`
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ItemHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
`

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const UnlinkButton = styled(TextButton)`
  min-height: 40px;
  padding: 0 4px;
  font-size: 14px;
  color: ${MUTED};
`

const Item = styled.li`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid #f4f4f5;
  border-radius: 8px;
`

const TaskLink = styled(Link)`
  font-size: 15px;
  font-weight: 600;
  color: ${LINK};
  text-decoration: underline;
  text-underline-offset: 3px;
`

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  font-size: 14px;
  color: ${MUTED};
`

const Status = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
`
